import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamId } from '@/hooks/useTeamId';

// Type for organization data (backward compatibility)
interface OrganizationData {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  website?: string;
  business_number?: string;
  business_type?: string;
  business_address?: any;
  logo_url?: string;
  email?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any; // Allow additional fields
}

export function useOrganization() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { teamId, loading: teamLoading } = useTeamId();

  // Get team data using the resolved teamId from useTeamId
  const { data: organization, isLoading, error } = useQuery({
    queryKey: ['team', user?.id],
    queryFn: async () => {
      if (!user?.id || !teamId) {
        return null;
      }

      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('id, name, description, industry, website, email, created_at, updated_at, business_structure, hq_location_city, hq_location_province, founded_year, annual_revenue, aum_total, num_advisors, num_employees, firm_valuation, firm_valuation_method, recurring_revenue_percentage, owner_dependency_percentage, client_count, owner_id, status, team_type, primary_dealer, business_address, business_registration_number')
        .eq('id', teamId)
        .single();

      if (teamError) {
        throw teamError;
      }

      return teamData as unknown as OrganizationData;
    },
    enabled: !!user?.id && !!teamId && !teamLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update team mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<any>) => {
      if (!organization?.id) throw new Error('No team found');

      const { data, error } = await supabase
        .from('teams')
        .update(updates as any)
        .eq('id', organization.id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as OrganizationData;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['team', user?.id], data);
    },
  });

  return {
    organization,
    isLoading: isLoading || teamLoading,
    error,
    updateOrganization: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
